import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import BrandHeading from "@/components/brand/BrandHeading";
import BrandLabel from "@/components/brand/BrandLabel";
import BrandButton from "@/components/brand/BrandButton";
import BrandCard from "@/components/brand/BrandCard";
import { fetchResidences } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

const BrionjHome = () => {
  const [res, setRes] = useState([]);
  useEffect(() => { fetchResidences().then(setRes); }, []);
  return (
    <div data-testid="brionj-home">
      {/* HERO */}
      <section className="relative" data-testid="brionj-hero">
        <div className="absolute inset-0 -z-10"><img src="https://images.unsplash.com/photo-1582610116397-edb318620f90?w=2400&q=80" alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-[hsl(var(--background))]/85" /></div>
        <div className="container-rail pt-20 pb-24 md:pt-32 md:pb-32">
          <BrandLabel withRule>The Brionj Collection</BrandLabel>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <BrandHeading level={1} className="mt-8 max-w-[14ch]">A residence. Held by <span>enquiry</span>.</BrandHeading>
            <p className="mt-8 max-w-[58ch] text-[17px] md:text-[18px] leading-[1.85] font-body editorial-measure text-foreground/85">Brionj is a small collection of private residences — four for now, held by enquiry. Stays are made by appointment; the house is then yours, with the staff to keep it as such.</p>
          </motion.div>
          <div className="mt-12 flex gap-3 flex-wrap">
            <BrandButton to="/brionj/collection" data-testid="brionj-explore">Plan a Stay</BrandButton>
            <BrandButton to="/brionj/collection" variant="ghost" data-testid="brionj-collection">View the collection →</BrandButton>
          </div>
        </div>
      </section>

      {/* Four residences preview */}
      <section data-testid="brionj-collection-preview">
        <div className="container-rail py-20 md:py-28">
          <div className="text-center">
            <BrandLabel withRule>The Collection</BrandLabel>
            <BrandHeading level={2} className="mt-6 max-w-[24ch] mx-auto">Four residences, four registers of stillness.</BrandHeading>
            <p className="mt-6 max-w-[60ch] mx-auto text-[15px] md:text-[16px] text-foreground/70 font-body editorial-measure leading-relaxed">Private villas, lodges and palacetes — each kept by a small resident staff, each available by enquiry rather than by booking.</p>
          </div>
          <div className="mt-16 space-y-16 md:space-y-24">
            {res.map((r, i) => (
              <motion.div key={r.id} initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.3 }} transition={{ duration: 0.7 }}>
                <div className="grid md:grid-cols-2 gap-10 items-center">
                  <div className={`p-6 md:p-8 bg-[hsl(var(--card))] border border-border ${i % 2 === 1 ? "md:order-2" : ""}`}>
                    <Link to={`/brionj/r/${r.id}`}><img src={r.hero_image} alt={r.name} className="w-full aspect-[4/3] object-cover" /></Link>
                  </div>
                  <div className={`${i % 2 === 1 ? "md:order-1" : ""}`}>
                    <div className="text-[11px] uppercase tracking-[0.42em] text-[hsl(var(--brand-accent))]">{String(i + 1).padStart(2, "0")}.</div>
                    <BrandLabel className="mt-2">{r.location}</BrandLabel>
                    <Link to={`/brionj/r/${r.id}`} className="mt-4 block font-display italic text-[36px] md:text-[44px] leading-[1.05] text-[hsl(var(--brand-accent))] hover:text-foreground transition-colors">{r.name}</Link>
                    <div className="mt-3 text-[12px] uppercase tracking-[0.3em] text-foreground/60">{r.bedrooms} bedrooms · sleeps {r.sleeps} {r.status === "forthcoming" && "· forthcoming"}</div>
                    <p className="mt-6 text-[15px] md:text-[16px] font-body leading-[1.85] editorial-measure text-foreground/80">{r.description}</p>
                    <Link to={`/brionj/r/${r.id}`} className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.4em] text-[hsl(var(--brand-accent))] hover:text-foreground transition-colors underline underline-offset-[10px] decoration-[hsl(var(--brand-accent))]" data-testid={`brionj-view-${r.id}`}>{r.status === "forthcoming" ? "Register interest" : "Discover"} →</Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote */}
      <section data-testid="brionj-quote">
        <div className="container-rail py-20 md:py-28 text-center">
          <div className="inline-block w-12 h-px bg-[hsl(var(--brand-accent))]" />
          <BrandHeading level={2} className="mt-8 max-w-[26ch] mx-auto">Here, the guests don't just <span>visit</span> — they truly arrive.</BrandHeading>
          <p className="mt-8 text-[12px] uppercase tracking-[0.42em] text-[hsl(var(--brand-accent))]">— The Brionj Editorial</p>
        </div>
      </section>

      {/* Notes */}
      <section data-testid="brionj-notes">
        <div className="container-rail pb-24 md:pb-32 grid md:grid-cols-2 gap-10">
          <div className="p-6 md:p-10 bg-[hsl(var(--card))] border border-border">
            <BrandLabel>The quiet hours</BrandLabel>
            <div className="mt-4 font-display italic text-[28px] md:text-[36px] leading-tight">A house manager on hospitality, by appointment.</div>
            <p className="mt-6 text-[14px] font-body leading-[1.85] text-foreground/70 editorial-measure">What changes when an entire house has been given to one family for a week; what the staff learn to keep silent and what to bring forward.</p>
          </div>
          <div className="p-6 md:p-10 bg-[hsl(var(--card))] border border-border">
            <BrandLabel>The atlas</BrandLabel>
            <div className="mt-4 font-display italic text-[28px] md:text-[36px] leading-tight">Where the next residence might be kept.</div>
            <p className="mt-6 text-[14px] font-body leading-[1.85] text-foreground/70 editorial-measure">A short atlas of houses we have looked at, kept, or politely passed on — and the qualities that make a house enter the collection.</p>
          </div>
        </div>
      </section>
    </div>
  );
};
export default BrionjHome;
